import i18next from "i18next";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";
import BasicInput from "../../components/BasicInput";
import Button from "../../components/Button";
import ConfirmDialog from "../../components/ConfirmDialog";
import ErrorMessage from "../../components/ErrorMessage";
import Label from "../../components/Label";
import SimpleDialog, { DialogPosition } from "../../components/SimpleDialog";
import * as locationsServices from "../../services/locationsServices";
import { setSelectedLocation } from "../../state/map/mapSlice";
import { constants, routes, utils } from "../../utils";
import { Location as LocationType } from "../../utils/types";
import AddLocationButtonsGroup from "../AddLocationButtonsGroup";
import SearchCoordinate from "../SearchCoordinate";

export default function Location() {
	const [location, setLocation] = React.useState<LocationType>({
		id: 0,
		name: "",
		coordinates: { lat: "0", lng: "0" },
	});
	const [errors, setErrors] = React.useState<any>({});
	const [editing, setEditing] = React.useState<boolean>(false);
	const [deleteModal, setDeleteModal] = React.useState<boolean>(false);
	const [selectingLocation, setSelectingLocation] = React.useState<boolean>(false);
	const [selectLocationMode, setSelectLocationMode] = React.useState<string>(
		constants.SELECT_LOCATION_MODE.enterLatitudeLongitude
	);
	const dispatch = useDispatch();
	const { id } = useParams();

	useEffect(() => {
		if (id) {
			locationsServices.getLocation(id).then((location) => {
				dispatch(setSelectedLocation(location.coordinates));
				setLocation(location);
			});
		}
		return () => {
			dispatch(setSelectedLocation(null));
		};
	}, [id]);

	const navigate = useNavigate();

	const goBack = () => navigate(routes.DASHBOARD);

	const validate = () => {
		const errors: any = {};

		if (!location.name) {
			errors.name = i18next.t("name-required");
		}

		setErrors(errors);

		return Object.keys(errors).length === 0 ? {} : errors;
	};

	const handelCancel = () => {
		if (editing) {
			setEditing(false);
		} else {
			navigate(-1);
		}
	};

	const handleCreateEditLocation = async () => {
		try {
			const errors = validate();
			if (Object.keys(errors).length !== 0) {
				return;
			}

			if (location.id) {
				await locationsServices.updateLocation(location);
			} else {
				await locationsServices.createLocation(location);
			}

			toast.success(i18next.t(editing ? "location-edit-success" : "location-create-success"));
			goBack();
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await locationsServices.deleteLocation(location.id);
			toast.success(i18next.t("location-delete-success"));
		} catch (error: any) {
			toast.error(i18next.t(error?.response?.data?.detail ?? "error"));
		}
		navigate(routes.LOCATIONS_LIST);
	};

	const showValue = !!id && !editing;

	if (selectingLocation) {
		return (
			<SearchCoordinate
				type={selectLocationMode}
				setSelectingLocation={setSelectingLocation}
				location={location}
				onChange={(e) => dispatch(setSelectedLocation(e.location))}
				handleAccept={({ id, lat, lng, name }) => {
					setLocation({ id, name, coordinates: { lat, lng } });
					setSelectingLocation(false);
				}}
				handleCancel={() => setSelectingLocation(false)}
			/>
		);
	}

	return (
		<SimpleDialog open={true} position={DialogPosition.Top} onClose={goBack}>
			<ConfirmDialog
				title={i18next.t("delete")}
				handleCancel={() => setDeleteModal(false)}
				handleConfirm={handleDelete}
				subtitle={i18next.t("location-delete-confirm")}
				open={deleteModal}
			/>

			<div className="d-flex justify-content-between">
				<Label
					isTitle
					text={
						id
							? editing
								? i18next.t("entity-edit", {
										entity: i18next.t("Location"),
								  })
								: i18next.t("location-details")
							: i18next.t("entity-create", {
									entity: i18next.t("Location"),
							  })
					}
				/>
				{editing && (
					<div
						className="text-danger"
						data-testid="delete-button"
						style={{ cursor: "pointer" }}
						onClick={() => setDeleteModal(true)}
					>
						{i18next.t("delete")}
					</div>
				)}
			</div>
			<BasicInput
				label={i18next.t("name")}
				value={location.name}
				required={true}
				onChange={(e) => setLocation({ ...location, name: e.target.value })}
				className="mt-2"
				error={errors.full_name}
				datatestid="name-input"
				showValue={showValue}
			/>
			{errors.name && <ErrorMessage error={errors.name} />}

			<AddLocationButtonsGroup
				setLocation={setLocation}
				location={location}
				creatingLocation={true}
				setSelectingLocation={setSelectingLocation}
				setSelectLocationMode={setSelectLocationMode}
				selectLocationMode={selectLocationMode}
				errors={errors}
				showValue={showValue}
			/>
			<div className="d-flex mt-4 justify-content-end">
				<Button label={i18next.t("cancel")} onClick={handelCancel} className="ms-4" secondary />
				{id ? (
					editing ? (
						<Button
							label={i18next.t("save")}
							datatestid="save-button"
							onClick={handleCreateEditLocation}
							className="ms-4"
						/>
					) : (
						<Button
							label={i18next.t("entity-edit", {
								entity: i18next.t("Location"),
							})}
							datatestid="edit-button"
							onClick={() => setEditing(true)}
							className="ms-4"
							visible={utils.isAdminOrCollaborator()}
						/>
					)
				) : (
					<Button
						label={i18next.t("entity-create", {
							entity: i18next.t("Location"),
						})}
						datatestid="create-button"
						onClick={handleCreateEditLocation}
						className="ms-4"
					/>
				)}
			</div>
		</SimpleDialog>
	);
}
